import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  ShieldCheck, Banknote, Globe2, Building2, Zap, Lock,
  TrendingUp, AlertTriangle, ArrowRight, CheckCircle2, Layers, Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Landing() {
  const [counts, setCounts] = useState({ banks: 175, carts: 125, marketplaces: 30, shipping: 20 });
  const [partners, setPartners] = useState({ banks: [], carts: [], marketplaces: [] });
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);

  const tryDemo = async (role = "merchant") => {
    setDemoLoading(true);
    try {
      const u = await demoLogin(role);
      toast.success(`Signed in as demo ${u.role}`);
      navigate(u.role === "admin" ? "/dashboard/admin" : "/dashboard");
    } catch (e) {
      toast.error("Demo unavailable. Try again.");
    } finally {
      setDemoLoading(false);
    }
  };

  useEffect(() => {
    api.get("/integrations").then(({ data }) => {
      setCounts(data.totals);
      setPartners({ banks: data.banks, carts: data.carts, marketplaces: data.marketplaces });
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900" data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 sk-grid-bg opacity-40" />
        <div className="absolute inset-0 sk-radial-glow" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fade}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-xs uppercase tracking-wider text-slate-600 mb-8" data-testid="hero-badge">
              <span className="w-2 h-2 rounded-full bg-[#00D533] animate-pulse" />
              <span>Approved-merchant payments network</span>
            </div>
          </motion.div>

          <motion.h1
            {...fade}
            transition={{ duration: 0.7, delay: 0.05, ease: "easeOut" }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight max-w-5xl"
            data-testid="hero-title"
          >
            Guaranteed Payments.{" "}
            <span className="text-[#00D533]">Hundreds of connectivity choices and features Globally.</span>{" "}
            <span className="text-slate-500">One Platform.</span>
          </motion.h1>

          <motion.p
            {...fade}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 max-w-2xl text-lg text-slate-500 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Whether you keep your own payments, risk, marketing or shipping providers — or use ours —
            we've got you covered. Transactions up to <span className="text-slate-900">$25,000+</span> on approved profiles.
          </motion.p>

          <motion.div
            {...fade}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link to="/register">
              <Button
                size="lg"
                className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold px-7 h-12 text-base"
                data-testid="hero-cta-apply"
              >
                Apply for merchant <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Button
              size="lg"
              onClick={() => tryDemo("merchant")}
              disabled={demoLoading}
              variant="ghost"
              className="border border-[#00D533]/40 text-[#00D533] hover:bg-[#00D533]/10 px-7 h-12 text-base"
              data-testid="hero-cta-demo"
            >
              <Play className="mr-2 w-4 h-4 fill-[#00D533]" /> {demoLoading ? "Loading..." : "Try the Demo"}
            </Button>
            <Link to="/integrations">
              <Button
                size="lg"
                variant="ghost"
                className="border border-slate-300 text-slate-900 hover:bg-slate-50 px-7 h-12 text-base"
                data-testid="hero-cta-integrations"
              >
                Browse integrations
              </Button>
            </Link>
          </motion.div>

          <motion.div {...fade} transition={{ delay: 0.32 }} className="mt-4 flex flex-wrap gap-3 text-xs text-slate-400">
            <button onClick={() => tryDemo("merchant")} className="hover:text-[#00D533] transition-colors underline-offset-4 hover:underline" data-testid="hero-demo-merchant">
              demo@sellecheck.com — merchant view
            </button>
            <span className="text-slate-300">·</span>
            <button onClick={() => tryDemo("admin")} className="hover:text-[#00D533] transition-colors underline-offset-4 hover:underline" data-testid="hero-demo-admin">
              admin@sellecheck.com — admin view
            </button>
          </motion.div>

          {/* Stat ticker */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
            {[
              { k: counts.banks, label: "Acquiring Banks" },
              { k: counts.carts, label: "Shopping Carts" },
              { k: counts.marketplaces, label: "Marketplaces" },
              { k: "$25K+", label: "Per-Tx Approval" },
            ].map((s, i) => (
              <div key={i} className="bg-[#F8FAFC] p-6" data-testid={`stat-tile-${i}`}>
                <div className="font-heading text-3xl md:text-4xl font-bold text-slate-900">{s.k}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-12 border-y border-slate-200 bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-6 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
            Connected to the global payments stack
          </p>
          <a
            href="https://SelleCheck.transactiongateway.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-[#0E2148] hover:text-[#C8A046] transition-colors"
            data-testid="landing-gateway-link"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#C8A046]" />
            SelleCheck.transactiongateway.com
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 3h7v7m0-7L10 14M5 5h4v2H7v10h10v-2h2v4H5V5z"/></svg>
          </a>
        </div>
        <div className="fade-side-mask">
          <Marquee gradient={false} speed={32} pauseOnHover>
            {(partners.banks.length ? partners.banks : ["Chase","HSBC","Citi","Wells Fargo","Adyen"]).slice(0, 60).map((b, i) => (
              <span key={i} className="mx-8 text-slate-400 text-sm tracking-wide font-mono whitespace-nowrap">{b}</span>
            ))}
          </Marquee>
        </div>
        <div className="fade-side-mask mt-4">
          <Marquee gradient={false} speed={28} direction="right" pauseOnHover>
            {(partners.carts.length ? partners.carts : ["Shopify","WooCommerce","Magento"]).slice(0, 60).map((b, i) => (
              <span key={i} className="mx-8 text-slate-300 text-sm tracking-wide font-mono whitespace-nowrap">{b}</span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* GATEWAY BAND */}
      <section className="py-14 bg-white border-y border-slate-200" data-testid="landing-gateway-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#00D533] font-semibold">PCI-DSS Level 1 Service Provider</p>
            <h3 className="font-heading text-2xl sm:text-3xl font-semibold mt-2 max-w-2xl text-slate-900">
              Card &amp; eCheck transactions run on{" "}
              <span className="text-[#00D533]">SelleCheck.transactiongateway.com</span> — separate login, certified environment.
            </h3>
            <p className="mt-3 text-slate-600 max-w-2xl text-sm">
              Cardholder data never touches our dashboard. Merchants connect once, then authenticate
              directly with the gateway each session for tokenization, vaulting, refunds and recurring billing.
            </p>
          </div>
          <div className="flex md:justify-end">
            <a
              href="https://SelleCheck.transactiongateway.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 h-12 rounded-md bg-[#00D533] hover:bg-[#00E636] text-black font-semibold transition-colors shadow-sm"
              data-testid="landing-gateway-cta"
            >
              Open Gateway
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 3h7v7m0-7L10 14M5 5h4v2H7v10h10v-2h2v4H5V5z"/></svg>
            </a>
          </div>
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section id="features" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fade} className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.18em] text-[#00D533] mb-4" data-testid="features-eyebrow">The Platform</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="features-title">
              A complete payments stack, engineered around the merchant.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Big card */}
            <motion.div {...fade} className="sk-card md:col-span-7 p-8 lg:p-10 relative overflow-hidden" data-testid="feature-guarantee">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#00D533]/10 border border-[#00D533]/30 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-[#00D533]" />
                </div>
                <span className="text-xs uppercase tracking-wider text-slate-500">SelleRisk · Bank-to-bank underwriting</span>
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight max-w-xl">
                SelleRisk underwrites almost any business and assumes the transaction risk.
              </h3>
              <p className="mt-4 text-slate-500 max-w-xl">
                Powered by <span className="font-semibold text-slate-900">Green.money</span> bank-to-bank rails.
                Settlement is guaranteed, chargeback exposure is removed from the merchant, and disputes are
                handled by SelleRisk — not by you.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
                {["Guaranteed settlement", "Assumed transaction risk", "Bank-to-bank rails (Green.money)", "No chargeback exposure"].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-[#00D533]" /> {t}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Side cards */}
            <motion.div {...fade} className="sk-card md:col-span-5 p-8" data-testid="feature-banks">
              <Building2 className="w-7 h-7 text-[#3B82F6] mb-5" />
              <h3 className="font-heading text-xl font-medium">175 acquiring banks. Keep yours.</h3>
              <p className="mt-3 text-slate-500 text-sm">
                Run on multiple bank merchant accounts. Continue with existing acquirers — SelleCheck
                routes intelligently across them.
              </p>
            </motion.div>

            <motion.div {...fade} className="sk-card md:col-span-4 p-8" data-testid="feature-international">
              <Globe2 className="w-7 h-7 text-[#00D533] mb-5" />
              <h3 className="font-heading text-xl font-medium">SelleMax — International Escrow.</h3>
              <p className="mt-3 text-slate-500 text-sm">
                Cross-border buyer/seller escrow at <span className="font-semibold text-slate-900">1.99%</span>.
                Funds held, released on delivery, disputes arbitrated, currency converted.
              </p>
            </motion.div>

            <motion.div {...fade} className="sk-card md:col-span-4 p-8" data-testid="feature-credit">
              <Banknote className="w-7 h-7 text-[#F59E0B] mb-5" />
              <h3 className="font-heading text-xl font-medium">Credit, eChecks &amp; cards.</h3>
              <p className="mt-3 text-slate-500 text-sm">
                Card processing, ACH/eCheck rails, and credit applications — gated by a unified risk profile.
              </p>
            </motion.div>

            <motion.div {...fade} className="sk-card md:col-span-4 p-8" data-testid="feature-ai">
              <Zap className="w-7 h-7 text-[#00D533] mb-5" />
              <h3 className="font-heading text-xl font-medium">AI risk + AML, per transaction.</h3>
              <p className="mt-3 text-slate-500 text-sm">
                Each charge is scored and explained — Claude-grade analysis with auditable reasoning.
              </p>
            </motion.div>

            <motion.div {...fade} className="sk-card md:col-span-7 p-8 lg:p-10 relative overflow-hidden" data-testid="feature-stack">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-slate-900" />
                </div>
                <span className="text-xs uppercase tracking-wider text-slate-500">The Integration Layer</span>
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight max-w-xl">
                {counts.banks} banks. {counts.carts} carts. Hundreds of marketplaces, shipping &amp; payments brands.
              </h3>
              <p className="mt-4 text-slate-500 max-w-lg">
                Plug in to the platforms you already use — without rebuilding a stack.
              </p>
              <Link to="/integrations" className="mt-8 inline-flex items-center gap-2 text-[#00D533] hover:underline" data-testid="feature-stack-link">
                Browse the directory <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div {...fade} className="sk-card md:col-span-5 p-8" data-testid="feature-volume">
              <TrendingUp className="w-7 h-7 text-[#3B82F6] mb-5" />
              <h3 className="font-heading text-xl font-medium">Up to $25,000+ per transaction</h3>
              <p className="mt-3 text-slate-500 text-sm">
                Approved profiles unlock high-ticket processing without daily caps that throttle growth.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* RISK SECTION */}
      <section className="py-24 border-t border-slate-200 bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-16 items-center">
          <motion.div {...fade}>
            <p className="text-xs uppercase tracking-[0.18em] text-[#FF4757] mb-4">Risk &amp; Chargebacks</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">
              Per-transaction underwriting, backed by bank-to-bank rails.
            </h2>
            <p className="mt-6 text-slate-500 max-w-lg leading-relaxed">
              Risk and AML are evaluated at the moment of authorization. Through SelleRisk (powered by
              Green.money), settlement is guaranteed and the transaction risk is assumed by SelleCheck —
              so merchants keep their revenue and skip the chargeback fight.
            </p>
            <ul className="mt-8 space-y-4 text-slate-600">
              {[
                "Per-tx AML treatment with explainable flags",
                "Velocity, geography & ticket-size signals",
                "SelleRisk assumes full transaction risk",
                "Evidence workflow with timeline tracking",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-[#00D533] mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fade} className="sk-card p-8 relative">
            <div className="flex items-center justify-between mb-6">
              <span className="text-xs uppercase tracking-wider text-slate-400">Live Risk Sample</span>
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#FF4757]/20 text-[#FF4757] uppercase tracking-wider drop-shadow-[0_0_8px_rgba(255,71,87,0.4)]">
                High Risk
              </span>
            </div>
            <div className="space-y-4 font-mono text-sm">
              <Row label="amount" value="$12,400.00" />
              <Row label="country" value="ID" />
              <Row label="rule_score" value="72 / 100" />
              <Row label="ai_action" value="manual_review" highlight />
            </div>
            <div className="mt-6 p-4 rounded-lg bg-[#FF4757]/10 border border-[#FF4757]/20">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#FF4757] mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700">
                  Single tx exceeds 50% of monthly volume; cross-border to non-allowlist country.
                  Recommend reviewer with manual KYC top-up.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section id="pricing" className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 sk-grid-bg opacity-30" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.h2 {...fade} className="font-heading text-4xl sm:text-6xl font-bold tracking-tight" data-testid="cta-title">
            Get approved. Get paid.
          </motion.h2>
          <motion.p {...fade} transition={{ delay: 0.1 }} className="mt-6 text-slate-500 max-w-xl mx-auto">
            No setup fees on standard tiers. Volume-based pricing reviewed quarterly. Apply once,
            run on your existing banks.
          </motion.p>
          <motion.div {...fade} transition={{ delay: 0.2 }} className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold px-8 h-12 text-base" data-testid="cta-apply-btn">
                Start your application <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="ghost" className="border border-slate-300 hover:bg-slate-50 px-8 h-12 text-base" data-testid="cta-login-btn">
                I already have an account
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <footer className="bg-[#F1F5F9] py-16 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-4 gap-10">
          <div>
            <div className="font-heading text-xl font-bold">Selle<span className="text-[#00D533]">Check</span></div>
            <p className="mt-3 text-slate-400 text-sm">
              Approved-merchant payments &amp; risk platform.
            </p>
          </div>
          <FooterCol title="Platform" items={["Card processing", "eCheck / ACH", "SelleRisk", "SelleMax Escrow"]} />
          <FooterCol title="Connect" items={[`${counts.banks} Banks`, `${counts.carts} Carts`, `${counts.marketplaces} Marketplaces`, `${counts.shipping}+ Shipping`]} />
          <FooterCol title="Account" items={["Apply", "Login", "Dashboard", "Admin"]} />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-12 pt-8 border-t border-slate-100 text-xs text-slate-300 flex flex-wrap justify-between gap-3">
          <div>© {new Date().getFullYear()} SelleCheck. All rights reserved.</div>
          <div className="font-mono">v1.0 — secure by design</div>
        </div>
      </footer>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
      <span className="text-slate-400">{label}</span>
      <span className={highlight ? "text-[#00D533]" : "text-slate-900"}>{value}</span>
    </div>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-400 mb-4">{title}</div>
      <ul className="space-y-2 text-sm text-slate-500">
        {items.map((i) => <li key={i}>{i}</li>)}
      </ul>
    </div>
  );
}
